import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import usePermissions from '../../../shared/hooks/usePermissions';
import useDebouncedValue from '../../../shared/hooks/useDebouncedValue';
import { getCurrentUser } from '../../../shared/utils/auth';
import { logger } from '../../../shared/utils/logger';
import { DEFAULT_META } from '../../../shared/constants/categories';
import { API_TICKET_STATUS } from '../../../shared/constants/statuses';
import {
  loadDashboardSummary,
  loadTicketList,
} from '../services/ticketDashboardService';
import {
  loadMasterData,
} from '../services/masterDataService';
import { mapDashboardSummaryToStats } from '../services/dashboardSummaryService';
import { createDefaultDashboardRange } from '../services/dashboardRangeService';

const SEARCH_DEBOUNCE_MS = 400;
const CARD_DEBOUNCE_MS = 200;
const EXPORT_PAGE_SIZE = 200;
const EXPORT_MAX_ROWS = 5000;
const ticketListCache = new Map();

const EMPTY_SUMMARY = Object.freeze({
  total: 0,
  overdue: 0,
  not_assigned: 0,
  by_status: [],
  by_category: [],
  by_pic: [],
  trend: [],
  volume_by_hour: [],
});

function errorMessage(error) {
  return error instanceof Error && error.message ? error.message : 'Failed to load ticket data.';
}

function summaryErrorMessage(error) {
  if (!error) return null;
  return `${error.message || 'Failed to load dashboard summary.'}${error.status ? ` (${error.status})` : ''}`;
}

function keyFor({ range, filters, cardFilter, limit, skip }) {
  return JSON.stringify({
    range: range?.mode === 'custom' ? [range.from, range.to] : range?.mode,
    filters,
    cardFilter: cardFilter || null,
    limit,
    skip,
  });
}

function cardStatusFilter(cardFilter) {
  const statusMaster = Object.values(API_TICKET_STATUS);
  return statusMaster.includes(cardFilter)
    ? [cardFilter]
    : undefined;
}

function cardFlagFilter(cardFilter) {
  return ['overdue', 'not_assigned'].includes(cardFilter) ? cardFilter : undefined;
}

function buildZeroCategoryData(categoriesList) {
  return (categoriesList || []).map(category => {
    const label = category.category_name || category.label || DEFAULT_META.label;
    return {
      id: category.id || label,
      categoryRefId: category.id || null,
      label,
      category_name: label,
      icon: category.icon || DEFAULT_META.icon,
      color: category.color || DEFAULT_META.color,
      count: 0,
    };
  });
}

export default function useRequests({
  filters = {}, categoryFilter = null, search = '', cardFilter = null, refreshKey = 0, summaryRefreshKey = 0,
} = {}) {
  const [allRequests, setAllRequests] = useState([]);
  const [dateRange, setDateRange] = useState(createDefaultDashboardRange);
  const [categoriesList, setCategoriesList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [statusesList, setStatusesList] = useState([]);
  const [categoryPicMapsList, setCategoryPicMapsList] = useState([]);
  const [masterDataStatus, setMasterDataStatus] = useState('idle');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [sidebarSummary, setSidebarSummary] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ limit: 20, skip: 0, hasNext: false, hasPrevious: false });

  const listControllerRef = useRef(null);
  const listGenerationRef = useRef(0);
  const pageLimitRef = useRef(20);
  const summaryControllerRef = useRef(null);
  const lastRefreshKeyRef = useRef(refreshKey);
  const lastSummaryRefreshKeyRef = useRef(summaryRefreshKey);
  const cardTimerRef = useRef(null);
  const masterDataGenerationRef = useRef(0);
  const argsRef = useRef(null);

  const { canViewAllTickets } = usePermissions();
  const userId = getCurrentUser().id;
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const queryFilters = useMemo(() => ({
    categoryRefId: categoryFilter || (filters.type !== 'all' ? filters.type : undefined),
    picId: filters.assignee,
    statuses: filters.status && filters.status !== 'all'
      ? [filters.status.replace('-', '_').toUpperCase()]
      : undefined,
    priorities: filters.priority && filters.priority !== 'all' ? [filters.priority.toUpperCase()] : undefined,
    search: debouncedSearch,
  }), [categoryFilter, filters.assignee, filters.priority, filters.status, filters.type, debouncedSearch]);

  argsRef.current = {
    canViewAllTickets,
    userId,
    dateRange,
    queryFilters,
    cardFilter,
    categoriesList,
    usersList,
    statusesList,
    categoryPicMapsList,
  };

  const requests = allRequests;

  // Stats come ONLY from /summary. While it is pending (or failed), show zeros.
  const summaryStats = useMemo(
    () => mapDashboardSummaryToStats(summary || EMPTY_SUMMARY, categoriesList),
    [summary, categoriesList],
  );

  const stats = useMemo(() => {
    if (summary) return summaryStats;
    return {
      ...summaryStats,
      categoryData: buildZeroCategoryData(categoriesList),
    };
  }, [summary, summaryStats, categoriesList]);

  const sidebarCategories = useMemo(() => ({
    total: Number(sidebarSummary?.total) || 0,
    items: mapDashboardSummaryToStats(sidebarSummary || EMPTY_SUMMARY, categoriesList)?.categoryData || [],
  }), [sidebarSummary, categoriesList]);

  const agents = useMemo(() => summaryStats?.agents || [], [summaryStats]);

  const fetchList = useCallback(async ({ skip = 0, limit = 20 } = {}) => {
    const args = argsRef.current;
    const cacheKey = keyFor({ range: args.dateRange, filters: args.queryFilters, cardFilter: args.cardFilter, limit, skip });
    const generation = listGenerationRef.current + 1;
    listGenerationRef.current = generation;
    listControllerRef.current?.abort();

    const cached = ticketListCache.get(cacheKey);
    if (cached) {
      if (skip >= cached.total && skip > 0) {
        return fetchList({ skip: Math.max(0, Math.ceil(cached.total / limit) - 1) * limit, limit });
      }
      setAllRequests(cached.requests);
      setTotal(cached.total);
      setPagination(cached.pagination);
      pageLimitRef.current = cached.pagination.limit;
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    listControllerRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const result = await loadTicketList({
        canViewAllTickets: args.canViewAllTickets,
        userId: args.userId,
        dateRange: args.dateRange,
        signal: controller.signal,
        limit,
        skip,
        ...args.queryFilters,
        statusFilters: cardStatusFilter(args.cardFilter) || args.queryFilters.statuses,
        flag: cardFlagFilter(args.cardFilter),
        categories: args.categoriesList,
        users: args.usersList,
        statuses: args.statusesList,
        categoryPicMaps: args.categoryPicMapsList,
      });
      if (generation !== listGenerationRef.current) return;
      if (skip >= result.total && skip > 0) {
        return fetchList({ skip: Math.max(0, Math.ceil(result.total / limit) - 1) * limit, limit });
      }
      ticketListCache.set(cacheKey, result);
      setAllRequests(result.requests);
      setTotal(result.total);
      setPagination(result.pagination);
      pageLimitRef.current = result.pagination.limit;
    } catch (loadError) {
      if (generation === listGenerationRef.current && loadError?.message !== 'Request was cancelled or timed out.') {
        setError(errorMessage(loadError));
      }
    } finally {
      if (generation === listGenerationRef.current) {
        setLoading(false);
        listControllerRef.current = null;
      }
    }
  }, []);

  // reset = true  → clear previous summary so KPI/donut show 0 while pending
  // reset = false → background refresh (notification/polling): keep current numbers
  const loadSummary = useCallback(async ({ reset = true } = {}) => {
    const args = argsRef.current;
    summaryControllerRef.current?.abort();
    const controller = new AbortController();
    summaryControllerRef.current = controller;
    setSummaryError(null);
    setSummaryLoading(true);
    if (reset) setSummary(null);
    try {
      const result = await loadDashboardSummary({
        dateRange: args.dateRange,
        ...args.queryFilters,
        signal: controller.signal,
      });
      if (!controller.signal.aborted) setSummary(result);
    } catch (loadError) {
      if (!controller.signal.aborted) setSummaryError(summaryErrorMessage(loadError));
    } finally {
      if (summaryControllerRef.current === controller) {
        setSummaryLoading(false);
        summaryControllerRef.current = null;
      }
    }
  }, []);

  const invalidateListCache = useCallback(() => ticketListCache.clear(), []);
  const reload = useCallback(({ skip = 0, limit = 20 } = {}) => fetchList({ skip, limit }), [fetchList]);
  const loadPage = useCallback((page, limit) => fetchList({ limit, skip: page * limit }), [fetchList]);

  // Refresh is used after a mutation. Never let a pre-mutation list snapshot
  // overwrite the optimistic row update with stale status/assignee data.
  // Reload the summary too (keeping current numbers while pending) so KPI
  // cards and donuts reflect the mutation, not just the table rows.
  const refresh = useCallback(({ skip = pagination.skip, limit = pagination.limit } = {}) => {
    invalidateListCache();
    setRefreshVersion(version => version + 1);
    return Promise.all([
      fetchList({ skip, limit }),
      loadSummary({ reset: false }),
    ]);
  }, [fetchList, invalidateListCache, loadSummary, pagination]);

  // Fetches every ticket matching the current filters/range/card filter, for
  // export. Pages through loadTicketList with a larger page size and never
  // touches ticketListCache (export is a one-off read, not a paginated view).
  const fetchAllForExport = useCallback(async ({ signal, onProgress } = {}) => {
    const args = argsRef.current;
    const limit = EXPORT_PAGE_SIZE;
    let skip = 0;
    let total = Infinity;
    const requests = [];

    while (skip < total) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const result = await loadTicketList({
        canViewAllTickets: args.canViewAllTickets,
        userId: args.userId,
        dateRange: args.dateRange,
        signal,
        limit,
        skip,
        ...args.queryFilters,
        statusFilters: cardStatusFilter(args.cardFilter) || args.queryFilters.statuses,
        flag: cardFlagFilter(args.cardFilter),
        categories: args.categoriesList,
        users: args.usersList,
        statuses: args.statusesList,
        categoryPicMaps: args.categoryPicMapsList,
      });
      total = Number(result.total) || 0;
      if (total > EXPORT_MAX_ROWS) {
        throw new Error(`Export exceeds the maximum of ${EXPORT_MAX_ROWS} tickets (${total} match the current filters).`);
      }
      requests.push(...result.requests);
      onProgress?.({ fetched: requests.length, total });
      if (!result.requests.length) break;
      skip += limit;
    }

    return { requests, total };
  }, []);

  const rangeKey = dateRange.mode === 'custom' ? `${dateRange.from}-${dateRange.to}` : dateRange.mode;
  const filtersKey = JSON.stringify(queryFilters);
  const listKey = `${rangeKey}|${filtersKey}|${cardFilter || ''}|${categoriesList.length}|${usersList.length}|${statusesList.length}|${categoryPicMapsList.length}`;

  useEffect(() => {
    const isBackgroundRefresh = lastSummaryRefreshKeyRef.current !== summaryRefreshKey;
    lastSummaryRefreshKeyRef.current = summaryRefreshKey;
    invalidateListCache();
    loadSummary({ reset: !isBackgroundRefresh });
  }, [rangeKey, filtersKey, canViewAllTickets, userId, summaryRefreshKey, loadSummary, invalidateListCache]);

  useEffect(() => {
    const controller = new AbortController();
    loadDashboardSummary({
      dateRange,
      signal: controller.signal,
    }).then(result => {
      if (!controller.signal.aborted) setSidebarSummary(result);
    }).catch(loadError => {
      if (!controller.signal.aborted) {
        logger.error('Failed to load unfiltered sidebar category summary.', loadError);
      }
    });
    return () => controller.abort();
  }, [rangeKey, summaryRefreshKey, refreshVersion, dateRange]);

  useEffect(() => {
    let active = true;
    const generation = masterDataGenerationRef.current + 1;
    masterDataGenerationRef.current = generation;
    setMasterDataStatus('loading');
    loadMasterData().then(data => {
      if (!active || generation !== masterDataGenerationRef.current) return;
      invalidateListCache();
      setCategoriesList(data.categories);
      setUsersList(data.users);
      setStatusesList(data.statuses);
      setCategoryPicMapsList(data.categoryPicMaps);
      setMasterDataStatus('ready');
    }).catch(loadError => {
      if (!active || generation !== masterDataGenerationRef.current) return;
      setAllRequests([]);
      setTotal(0);
      setMasterDataStatus('error');
      logger.error(
        'Failed to load master data (categories, statuses, users, category-pic-maps).',
        loadError,
      );
    });
    return () => { active = false; };
  }, [invalidateListCache]);

  const retryMasterData = useCallback(() => {
    const generation = masterDataGenerationRef.current + 1;
    masterDataGenerationRef.current = generation;
    setMasterDataStatus('loading');
    setAllRequests([]);
    setTotal(0);
    return loadMasterData({ force: true }).then(data => {
      if (generation !== masterDataGenerationRef.current) return data;
      invalidateListCache();
      setCategoriesList(data.categories);
      setUsersList(data.users);
      setStatusesList(data.statuses);
      setCategoryPicMapsList(data.categoryPicMaps);
      setMasterDataStatus('ready');
      return data;
    }).catch(loadError => {
      if (generation !== masterDataGenerationRef.current) throw loadError;
      setMasterDataStatus('error');
      logger.error(
        'Failed to load master data (categories, statuses, users, category-pic-maps).',
        loadError,
      );
      throw loadError;
    });
  }, [invalidateListCache]);

  useEffect(() => {
    if (masterDataStatus !== 'ready') return undefined;
    if (lastRefreshKeyRef.current !== refreshKey) {
      lastRefreshKeyRef.current = refreshKey;
      invalidateListCache();
    }
    clearTimeout(cardTimerRef.current);
    cardTimerRef.current = setTimeout(() => fetchList({ skip: 0, limit: pageLimitRef.current }), CARD_DEBOUNCE_MS);
    return () => clearTimeout(cardTimerRef.current);
  }, [listKey, refreshKey, fetchList, invalidateListCache, masterDataStatus]);

  useEffect(() => () => {
    listControllerRef.current?.abort();
    summaryControllerRef.current?.abort();
  }, []);

  const setRequests = setAllRequests;

  return {
    requests,
    setRequests,
    allRequestCount: total || allRequests.length,
    dateRange,
    setDateRange: next => { invalidateListCache(); setDateRange(next); },
    stats,
    sidebarCategories,
    agents,
    loading,
    error,
    summaryError,
    summaryLoading,
    masterDataStatus,
    retryMasterData,
    pagination,
    summary,
    loadPage,
    reload,
    refresh,
    fetchAllForExport,
    ticketCategories: categoriesList,
    ticketStatuses: statusesList,
  };
}
